import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import DashboardLayout from "@/layouts/DashboardLayout";
import DashboardPage from "@/pages/DashboardPage";
import FormEditorPage from "@/pages/FormEditorPage";
import ArchivePage from "@/pages/ArchivePage";
import SettingsPage from "@/pages/SettingsPage";
import InventoryPage from "@/pages/InventoryPage";
import { AuthContext } from "@/contexts/AuthContext";
import { getMe } from "@/lib/api";

function App() {
  const [auth, setAuth] = useState({ username: null, role: null });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setIsLoading(false);
      return;
    }
    // Hydrate from cached values immediately so UI doesn't flash
    const cachedRole = localStorage.getItem("role");
    const cachedUser = localStorage.getItem("username");
    if (cachedRole && cachedUser) {
      setAuth({ username: cachedUser, role: cachedRole });
    }
    // Verify token + refresh role from backend
    getMe()
      .then((res) => {
        const u = res.data.username;
        const r = res.data.role || "admin";
        localStorage.setItem("username", u);
        localStorage.setItem("role", r);
        setAuth({ username: u, role: r });
      })
      .catch(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("role");
        localStorage.removeItem("username");
        setAuth({ username: null, role: null });
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleLogin = ({ token, username, role }) => {
    localStorage.setItem("token", token);
    localStorage.setItem("username", username);
    localStorage.setItem("role", role);
    setAuth({ username, role });
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("username");
    setAuth({ username: null, role: null });
  };

  const isAuthenticated = !!auth.role;
  const isViewer = auth.role === "viewer";
  const isAdmin = auth.role === "admin";

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ username: auth.username, role: auth.role, isAdmin, isViewer }}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route
              path="/login"
              element={
                isAuthenticated ? (
                  <Navigate to={isViewer ? "/inventory" : "/"} replace />
                ) : (
                  <LoginPage onLogin={handleLogin} />
                )
              }
            />
            <Route
              path="/*"
              element={
                isAuthenticated ? (
                  <DashboardLayout onLogout={handleLogout}>
                    <Routes>
                      <Route
                        index
                        element={isViewer ? <Navigate to="/inventory" replace /> : <DashboardPage />}
                      />
                      <Route
                        path="form/new"
                        element={isViewer ? <Navigate to="/inventory" replace /> : <FormEditorPage />}
                      />
                      <Route
                        path="form/:id"
                        element={isViewer ? <Navigate to="/archive" replace /> : <FormEditorPage />}
                      />
                      <Route path="archive" element={<ArchivePage />} />
                      <Route path="inventory" element={<InventoryPage />} />
                      <Route
                        path="settings"
                        element={isViewer ? <Navigate to="/inventory" replace /> : <SettingsPage />}
                      />
                    </Routes>
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
